import { BLEND_MODES, Container, Graphics, LINE_JOIN, Point, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { chamfer, FACE, INK, RIM_LIGHT, TIMBER, TIMBER_EDGE } from "./build_board"
import { filledSocket, glowDisc, REWARD_EMBER, REWARD_GOLD, REWARD_GOLD_HOT, REWARD_INK_DARK, REWARD_WHITE, shineBand,
    spark, sparkBurst } from "./chapter_reward_art"
import { Eased, easeOutCubic, fanfarePose, fanfareTimeline, lerp, quadPoint, span, toastPose, toastTimeline, TOAST_HOLD_MS,
    TOAST_HOLD_QUEUED_MS } from "./chapter_reward_motion"
import { getUiMetrics } from "./ui_metrics"

// The chapter checklist's rewards, played over the whole screen (chapter_checklist.ts is the list
// they land in).
//
// A task done is three beats: a plaque pops in the upper middle of the screen -- where the player is
// looking, not in the corner the list lives in -- a ring of sparks leaves it, and its gold diamond
// then flies across the screen into the task's row, which stamps the check on arrival. A chapter done
// is the FANFARE: a flash and shockwave, rays opening behind the chapter's title, a COMPLETE stamp
// slammed across it and embers climbing past, then the whole banner draws into a comet and flies into
// the checklist panel, where "Continue Story" is waiting.
//
// WHERE: low in the middle of the screen by default, below the characters' feet -- speech bubbles
// hang ABOVE heads, and the Lord says something at nearly every one of these moments, so the upper
// middle (the obvious place for a toast) is exactly where his line is. Each beat also checks the
// live bubbles and anything else it is told to avoid (the building helper) and takes the first slot
// clear of them; if a bubble opens over it while it holds, it glides to another.
//
// Beats queue and play one at a time, so the last task's stamp lands before the fanfare goes up. A
// toast with others waiting behind it holds for less. Nothing here takes input: the layer is
// decoration over a world that keeps running.
//
// Every pose is a pure function of the beat's clock (chapter_reward_motion.ts), advanced by animate()
// from GuiManager.updateTimer. No filters: glows are stacked fills on ADD layers.

export type CelebrationCue = "task" | "stamp" | "chapter"

type Target = () => Point | undefined

interface Beat {
    kind: "task" | "chapter"
    title: string
    chapter?: number
    target: Target
    onArrive: () => void
    ms: number
    hold: number
    arrived: boolean
    from?: { x: number; y: number }
    trail: { x: number; y: number }[]
}

interface Mote { x: number; y: number; vx: number; vy: number; age: number; life: number; r: number }

const TOAST_H = 48
const TOAST_SOCKET_X = 26
const TOAST_TEXT_X = 46
const TOAST_SOCKET_R = 9
const TRAIL = 12
const MOTE_EVERY_MS = 40
const MOTE_MAX = 40
const RAYS = 16

type Rect = { x: number; y: number; w: number; h: number }
// Candidate centres as fractions of the viewport, in order of preference: below the characters
// first, then above them, then off to either side.
const TOAST_SLOTS: [number, number][] = [[0.5, 0.72], [0.5, 0.21], [0.28, 0.72], [0.72, 0.72], [0.28, 0.21], [0.72, 0.21]]
const BANNER_SLOTS: [number, number][] = [[0.5, 0.68], [0.5, 0.29]]
const SLOT_MARGIN = 14
const GLIDE_MS = 320

const overlapArea = (a: Rect, b: Rect) =>
    Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y))

/** The first slot clear of every obstacle, else the one overlapping them least (earlier wins ties). */
export const pickSlot = (slots: { x: number; y: number }[], w: number, h: number, obstacles: Rect[]) => {
    let best = 0, bestArea = Infinity
    slots.forEach((slot, index) => {
        const box = { x: slot.x - w / 2, y: slot.y - h / 2, w, h }
        const area = obstacles.reduce((sum, obstacle) => sum + overlapArea(box, obstacle), 0)
        if (area < bestArea) { best = index; bestArea = area }
    })
    return { index: best, area: bestArea }
}

export class ChapterCelebration extends Container {
    private queue: Beat[] = []
    private beat?: Beat
    private glow = new Graphics()
    private backing = new Graphics()
    private stage_ = new Container()
    private plate = new Graphics()
    private fx = new Graphics()
    private cometBody = new Graphics()
    private texts: Text[] = []
    private motes: Mote[] = []
    private moteClock = 0
    private toastW = 220
    private titleBox = { x: 0, y: 0, w: 0, h: 0 }
    private stampText?: Text
    private stampW = 0
    private atX = new Eased(0, GLIDE_MS)
    private atY = new Eased(0, GLIDE_MS)
    private slot = 0

    constructor(private onCue: (cue: CelebrationCue) => void = () => undefined,
        private viewport: () => { left: number; top: number; width: number; height: number } = () => getUiMetrics().safeViewport,
        private avoid: () => Rect[] = () => []) {
        super()
        this.interactive = false
        this.interactiveChildren = false
        this.glow.blendMode = BLEND_MODES.ADD
        this.fx.blendMode = BLEND_MODES.ADD
        this.stage_.addChild(this.plate)
        this.addChild(this.backing, this.glow, this.stage_, this.fx, this.cometBody)
        this.visible = false
    }

    /** A task finished: toast it, then fly its diamond to `target` and call `onArrive` there. */
    taskComplete = (label: string, target: Target, onArrive: () => void) => {
        this.enqueue({ kind: "task", title: label, target, onArrive, ms: 0, hold: TOAST_HOLD_MS, arrived: false, trail: [] })
    }

    /** The chapter finished: the fanfare, docking into `target`. */
    chapterComplete = (chapter: number, title: string, target: Target, onArrive: () => void) => {
        this.enqueue({ kind: "chapter", chapter, title, target, onArrive, ms: 0, hold: 0, arrived: false, trail: [] })
    }

    isBusy = () => !!this.beat || this.queue.length > 0

    /** Room change: land everything at once, so no row is left unchecked and no button unreachable. */
    flush = () => {
        const beats = [...(this.beat ? [this.beat] : []), ...this.queue]
        this.queue = []
        this.endBeat()
        beats.forEach(beat => { if (!beat.arrived) beat.onArrive() })
    }

    private enqueue = (beat: Beat) => {
        this.queue.push(beat)
        if (!this.beat) this.startNext()
    }

    private startNext = () => {
        this.endBeat()
        const beat = this.queue.shift()
        if (!beat) return
        this.beat = beat
        this.visible = true
        if (beat.kind === "task") {
            this.buildToast(beat)
            this.onCue("task")
        } else {
            this.buildBanner(beat)
            this.onCue("chapter")
        }
        this.settle(beat)
    }

    private endBeat = () => {
        this.beat = undefined
        this.texts.forEach(text => { if (!text.destroyed) text.destroy() })
        this.texts = []
        this.stampText = undefined
        this.motes = []
        this.plate.clear(); this.glow.clear(); this.fx.clear(); this.cometBody.clear(); this.backing.clear()
        this.stage_.scale.set(1)
        this.stage_.alpha = 1
        this.visible = false
    }

    private text = (value: string, color: number, size: number, bold: boolean, spacing = 0, stroke = 0) => {
        const text = new TextRenderer().fontSize(size).bold(bold).color(color).strokeThickness(stroke).render(value) as Text
        if (spacing) text.style.letterSpacing = spacing
        this.texts.push(text)
        this.stage_.addChild(text)
        return text
    }

    /** The beat's footprint, for keeping it clear of bubbles. */
    private footprint = (beat: Beat) => beat.kind === "task"
        ? { w: this.toastW + SLOT_MARGIN * 2, h: TOAST_H + SLOT_MARGIN * 2 }
        : { w: Math.min(this.viewport().width, this.titleBox.w + 80), h: 120 }

    private slotCentres = (beat: Beat) => {
        const view = this.viewport()
        return (beat.kind === "task" ? TOAST_SLOTS : BANNER_SLOTS)
            .map(([fx, fy]) => ({ x: view.left + view.width * fx, y: view.top + view.height * fy }))
    }

    /** Obstacles arrive in global coordinates; this layer draws in its own. */
    private obstacles = (): Rect[] => this.avoid().map(rect => {
        const a = this.toLocal(new Point(rect.x, rect.y))
        const b = this.toLocal(new Point(rect.x + rect.w, rect.y + rect.h))
        return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) }
    })

    /** Where the beat stands this frame, gliding to a clear slot if a bubble has opened over it. */
    private center = (beat: Beat, settling: boolean) => {
        const centres = this.slotCentres(beat)
        const { w, h } = this.footprint(beat)
        if (settling) {
            const obstacles = this.obstacles()
            const here = centres[this.slot] || centres[0]
            const hereArea = pickSlot([here], w, h, obstacles).area
            if (hereArea > 0) {
                const best = pickSlot(centres, w, h, obstacles)
                if (best.area < hereArea && best.index !== this.slot) {
                    this.slot = best.index
                    this.atX.set(centres[best.index].x, beat.ms)
                    this.atY.set(centres[best.index].y, beat.ms)
                }
            }
        }
        return { x: this.atX.value(beat.ms), y: this.atY.value(beat.ms) }
    }

    private settle = (beat: Beat) => {
        const centres = this.slotCentres(beat)
        const { w, h } = this.footprint(beat)
        this.slot = pickSlot(centres, w, h, this.obstacles()).index
        this.atX.snap(centres[this.slot].x)
        this.atY.snap(centres[this.slot].y)
    }

    // ---- The toast ----------------------------------------------------------------------------

    private buildToast = (beat: Beat) => {
        const overline = this.text("TASK COMPLETE", REWARD_GOLD, 8, true, 1.8)
        const label = this.text(beat.title, INK, 13, true)
        this.toastW = Math.max(210, Math.ceil(Math.max(label.width, overline.width) + TOAST_TEXT_X + 22))
        overline.position.set(TOAST_TEXT_X, 10)
        label.position.set(TOAST_TEXT_X, 22)
        const g = this.plate.clear()
        const w = this.toastW, h = TOAST_H
        g.beginFill(0x000000, 0.35)
        chamfer(g, 0, 4, w, h, 7)
        g.endFill()
        g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 0.95, join: LINE_JOIN.MITER })
        g.beginFill(TIMBER, 0.97)
        chamfer(g, 0, 0, w, h, 7)
        g.endFill()
        g.lineStyle({ width: 1, color: REWARD_GOLD_HOT, alpha: 0.9, join: LINE_JOIN.MITER })
        g.beginFill(FACE, 0.96)
        chamfer(g, 4, 4, w - 8, h - 8, 5)
        g.endFill()
        g.lineStyle(1, RIM_LIGHT, 0.22).moveTo(11, 5.5).lineTo(w - 11, 5.5)
        g.lineStyle(0)
        filledSocket(g, TOAST_SOCKET_X, h / 2, TOAST_SOCKET_R)
        this.stage_.pivot.set(w / 2, h / 2)
    }

    private animateToast = (beat: Beat) => {
        const pose = toastPose(beat.ms, beat.hold)
        const { x, y: toastY } = this.center(beat, beat.ms < toastTimeline(beat.hold).leaveAt)
        this.stage_.position.set(x, toastY + pose.lift)
        this.stage_.scale.set(pose.scale)
        this.stage_.alpha = Math.max(0, pose.alpha)
        // Where the toast's socket is on screen, for the comet to set off from.
        const socket = { x: x + (TOAST_SOCKET_X - this.toastW / 2) * pose.scale, y: toastY + pose.lift }
        const fx = this.fx.clear()
        const glow = this.glow.clear()
        if (pose.alpha > 0) {
            glowDisc(glow, x, toastY + pose.lift, this.toastW * 0.62 * pose.scale, REWARD_GOLD,
                0.22 * pose.alpha * (1 - pose.burstT * 0.5), 5)
            glowDisc(glow, socket.x, socket.y, 22, REWARD_GOLD_HOT, 0.6 * pose.alpha, 4)
        }
        sparkBurst(fx, socket.x, socket.y, pose.burstT, 14, 12, 46, REWARD_GOLD_HOT, 0.3, 2)
        if (pose.burstT > 0 && pose.burstT < 1) {
            const t = easeOutCubic(pose.burstT)
            fx.lineStyle({ width: 2.5 * (1 - pose.burstT) + 0.5, color: REWARD_GOLD, alpha: 1 - pose.burstT, join: LINE_JOIN.MITER })
            fx.drawEllipse(x, toastY + pose.lift, (this.toastW / 2 + 6) + 40 * t, TOAST_H / 2 + 6 + 22 * t)
            fx.lineStyle(0)
        }
        if (pose.shineT > 0 && pose.shineT < 1 && pose.alpha > 0.5) {
            const left = x - (this.toastW / 2) * pose.scale, top = toastY + pose.lift - (TOAST_H / 2) * pose.scale
            shineBand(fx, left + 5, top + 5, (this.toastW - 10) * pose.scale, (TOAST_H - 10) * pose.scale, pose.shineT, 40, 0.3)
        }
        this.flyComet(beat, pose.cometT, socket, TOAST_SOCKET_R, 6)
        if (pose.arrived && !beat.arrived) this.arrive(beat)
        if (beat.ms >= toastTimeline(beat.hold).endAt) this.startNext()
    }

    // ---- The fanfare --------------------------------------------------------------------------

    private buildBanner = (beat: Beat) => {
        const overline = this.text(`CHAPTER ${beat.chapter}`, RIM_LIGHT, 12, true, 5, 3)
        const title = this.text(beat.title, INK, 30, true, 0.5, 4)
        const stamp = this.stampText = this.text("COMPLETE", REWARD_INK_DARK, 13, true, 4)
        overline.anchor.set(0.5, 0.5)
        title.anchor.set(0.5, 0.5)
        stamp.anchor.set(0.5, 0.5)
        overline.position.set(0, -36)
        title.position.set(0, 0)
        stamp.position.set(0, 40)
        this.titleBox = { x: -title.width / 2, y: -title.height / 2, w: title.width, h: title.height }
        this.stampW = stamp.width + 26
        this.stage_.pivot.set(0, 0)
    }

    private animateBanner = (beat: Beat) => {
        const pose = fanfarePose(beat.ms)
        const { x, y } = this.center(beat, beat.ms < fanfareTimeline().dockAt - 300)
        const view = this.viewport()
        // Docking: the banner draws in on itself while its heart flies off as the comet.
        this.stage_.position.set(x, y)
        this.stage_.scale.set(pose.bannerScale)
        this.stage_.alpha = pose.bannerAlpha

        // The shadow ribbon the type stands on, so it reads over grass, snow or night alike.
        const backing = this.backing.clear()
        const ribbon = Math.min(view.width, 620)
        const bandAlpha = 0.55 * Math.min(pose.titleAlpha, pose.bannerAlpha)
        for (let i = 0; i < 10; i++) {
            const k = i / 10
            backing.beginFill(0x0b0705, bandAlpha / 10)
                .drawRect(x - ribbon / 2 * (1 - k * 0.6), y - 62 + k * 6, ribbon * (1 - k * 0.6), 124 - k * 12).endFill()
        }

        const glow = this.glow.clear()
        const fx = this.fx.clear()
        // The flash and the shockwave.
        if (pose.flash > 0) glowDisc(glow, x, y, 260, REWARD_WHITE, 0.55 * pose.flash, 6)
        if (pose.ringT > 0 && pose.ringT < 1) {
            const t = easeOutCubic(pose.ringT)
            for (const [lag, width] of [[0, 4], [0.12, 2]] as const) {
                const k = Math.max(0, t - lag)
                fx.lineStyle({ width: width * (1 - pose.ringT) + 0.5, color: REWARD_GOLD, alpha: (1 - pose.ringT) * 0.9 })
                fx.drawEllipse(x, y, 60 + 300 * k, (60 + 300 * k) * 0.42)
            }
            fx.lineStyle(0)
        }
        // Rays opening behind the title, turning slowly while it holds.
        if (pose.raysAlpha > 0) {
            const turn = beat.ms / 9000 * Math.PI * 2
            // Short enough to stay under the speakers standing above the banner.
            const reach = 205 * pose.raysOpen
            for (let i = 0; i < RAYS; i++) {
                const a = turn + (i / RAYS) * Math.PI * 2
                const half = (i % 2 ? 0.05 : 0.09)
                const len = reach * (i % 2 ? 0.75 : 1)
                glow.beginFill(i % 3 ? REWARD_GOLD : REWARD_EMBER, 0.13 * pose.raysAlpha)
                glow.moveTo(x, y)
                    .lineTo(x + Math.cos(a - half) * len, y + Math.sin(a - half) * len * 0.62)
                    .lineTo(x + Math.cos(a + half) * len, y + Math.sin(a + half) * len * 0.62)
                    .closePath()
                glow.endFill()
            }
            glowDisc(glow, x, y, 170 * pose.raysOpen, REWARD_GOLD, 0.45 * pose.raysAlpha, 6)
        }
        // The type.
        const [overline, title] = this.texts
        if (overline) overline.alpha = pose.overlineAlpha
        if (title) { title.alpha = pose.titleAlpha; title.scale.set(pose.titleScale) }
        // The rule under the title and the plate the stamp is on are drawn into the moving stage.
        const plate = this.plate.clear()
        if (pose.rule > 0) {
            const half = (this.titleBox.w / 2 + 30) * pose.rule
            plate.lineStyle(2, REWARD_GOLD, 0.9).moveTo(-half, 20).lineTo(half, 20)
            plate.lineStyle(0)
            plate.beginFill(REWARD_GOLD, 1)
            spark(plate, -half, 20, 4)
            spark(plate, half, 20, 4)
            plate.endFill()
        }
        if (this.stampText) {
            this.stampText.alpha = pose.stampAlpha
            this.stampText.scale.set(pose.stampScale)
            this.stampText.rotation = -0.05
            if (pose.stampScale > 0) {
                const w = this.stampW * pose.stampScale, h = 22 * pose.stampScale
                plate.lineStyle({ width: 1.5, color: 0x6b4a14, alpha: pose.stampAlpha, join: LINE_JOIN.MITER })
                plate.beginFill(REWARD_GOLD, pose.stampAlpha)
                const c = Math.cos(-0.05), s = Math.sin(-0.05)
                const corners = [[-w / 2 + 6, -h / 2], [w / 2 - 6, -h / 2], [w / 2, 0], [w / 2 - 6, h / 2], [-w / 2 + 6, h / 2], [-w / 2, 0]]
                plate.drawPolygon(corners.flatMap(([px, py]) => [px * c - py * s, 40 + px * s + py * c]))
                plate.endFill()
                plate.lineStyle(0)
            }
        }
        sparkBurst(fx, x, y + 40, pose.stampBurstT, 18, 30, 110, REWARD_GOLD_HOT, 0.2, 2.2)
        sparkBurst(fx, x, y, span(beat.ms, 0, 700), 24, 40, 200, REWARD_GOLD, 0, 2.5)
        if (pose.shineT > 0 && pose.shineT < 1 && pose.bannerAlpha > 0.5) {
            shineBand(fx, x + this.titleBox.x * pose.titleScale, y + this.titleBox.y * pose.titleScale,
                this.titleBox.w * pose.titleScale, this.titleBox.h * pose.titleScale, pose.shineT, 60, 0.42)
        }
        this.animateMotes(fx, x, y, pose.embers)
        this.flyComet(beat, pose.cometT, { x, y }, 12, 8)
        if (pose.arrived && !beat.arrived) this.arrive(beat)
        if (beat.ms >= fanfareTimeline().endAt) this.startNext()
    }

    private animateMotes = (g: Graphics, cx: number, cy: number, emitting: boolean) => {
        if (emitting) {
            while (this.moteClock >= MOTE_EVERY_MS && this.motes.length < MOTE_MAX) {
                this.moteClock -= MOTE_EVERY_MS
                this.motes.push({ x: cx + (Math.random() - 0.5) * 420, y: cy + 40 + Math.random() * 30,
                    vx: (Math.random() - 0.5) * 0.02, vy: -(0.04 + Math.random() * 0.06), age: 0,
                    life: 1100 + Math.random() * 900, r: 0.8 + Math.random() * 1.6 })
            }
        }
        this.motes = this.motes.filter(mote => mote.age < mote.life)
        this.motes.forEach(mote => {
            const k = mote.age / mote.life
            const alpha = Math.sin(Math.PI * k)
            g.beginFill(k < 0.5 ? REWARD_GOLD : REWARD_EMBER, 0.85 * alpha).drawCircle(mote.x, mote.y, mote.r).endFill()
            g.beginFill(REWARD_GOLD, 0.18 * alpha).drawCircle(mote.x, mote.y, mote.r * 3).endFill()
        })
    }

    // ---- The comet ----------------------------------------------------------------------------

    private targetLocal = (beat: Beat) => {
        const global = beat.target()
        return global ? this.toLocal(global) : undefined
    }

    /** The diamond (or the banner's heart) flying into the checklist, trailing light. */
    private flyComet = (beat: Beat, t: number, from: { x: number; y: number }, r0: number, r1: number) => {
        const body = this.cometBody.clear()
        if (t < 0 || beat.arrived) return
        if (!beat.from) beat.from = { ...from }
        const target = this.targetLocal(beat)
        if (!target) return
        // Bow up and over: a thrown thing, not a slide.
        const via = { x: (beat.from.x + target.x) / 2, y: Math.min(beat.from.y, target.y) - 60 }
        const head = quadPoint(beat.from, via, target, t)
        beat.trail.unshift(head)
        if (beat.trail.length > TRAIL) beat.trail.length = TRAIL
        const fx = this.fx
        beat.trail.forEach((p, i) => {
            const k = 1 - i / TRAIL
            fx.beginFill(i < 3 ? REWARD_WHITE : REWARD_GOLD, 0.5 * k).drawCircle(p.x, p.y, Math.max(1, lerp(r1, r0, 1 - t) * 0.8 * k)).endFill()
            fx.beginFill(REWARD_EMBER, 0.12 * k).drawCircle(p.x, p.y, lerp(r1, r0, 1 - t) * 2.2 * k).endFill()
        })
        glowDisc(fx, head.x, head.y, 22, REWARD_GOLD, 0.7, 4)
        filledSocket(body, head.x, head.y, lerp(r0, r1, t))
    }

    private arrive = (beat: Beat) => {
        beat.arrived = true
        this.cometBody.clear()
        beat.onArrive()
        this.onCue("stamp")
    }

    /** Frame-driven; deltaTime in ms. */
    animate = (deltaTime: number) => {
        const beat = this.beat
        if (!beat) return
        const dt = Math.max(0, Math.min(100, deltaTime || 0))
        beat.ms += dt
        this.moteClock += dt
        this.motes.forEach(mote => { mote.age += dt; mote.x += mote.vx * dt; mote.y += mote.vy * dt })
        // A toast with others queued behind it says its piece faster.
        if (beat.kind === "task") beat.hold = this.queue.length ? Math.min(beat.hold, TOAST_HOLD_QUEUED_MS) : beat.hold
        if (beat.kind === "task") this.animateToast(beat)
        else this.animateBanner(beat)
    }

    destroy = () => {
        this.endBeat()
        super.destroy({ children: true })
    }
}
